/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { Dispatch, ReactNode, SetStateAction } from 'react';
import SearchWithNavigation from '@/ux-common/src/components/common/search-with-navigation';
import { useSearchHighlightText } from '@/ux-common/src/hook/useSearchHighlightText';
import { kahuCopyInfoType } from 'types/kahu';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { KahuIcons } from '@/components/icons/icons';

import { formatMaybeJson, PromptBlock } from './prompt-list-utils';
import { TraceIdLink } from './trace-id-link';

// Helper component to display a section (Input/Output) in the modal with highlighted search results
function Section({
  label,
  nodes,
}: {
  label: string;
  nodes: ReactNode[] | null;
}) {
  if (!nodes) return null;
  return (
    <div className="flex flex-col gap-1.5 rounded-lg bg-[#08242F] px-4 py-2">
      <span className="text-body-sm font-medium text-body-subtle">{label}</span>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[12px] text-body-secondary">
        {nodes}
      </pre>
    </div>
  );
}

export function PromptModal({
  block,
  occurrenceKey,
  copiedInfo,
  setCopiedInfo,
  onClose,
}: {
  block: PromptBlock;
  occurrenceKey: string;
  copiedInfo: kahuCopyInfoType | null;
  setCopiedInfo: Dispatch<SetStateAction<kahuCopyInfoType | null>>;
  onClose: () => void;
}) {
  const {
    term,
    setTerm,
    handleClearSearch,
    prevMatch,
    nextMatch,
    matches,
    currentIndex,
    beginHighlightPass,
    getHighlightedNodes,
  } = useSearchHighlightText([block.input, block.output]);

  beginHighlightPass();
  const inputFormatted = formatMaybeJson(block.input);
  const outputFormatted = formatMaybeJson(block.output);
  const inputNodes = inputFormatted
    ? getHighlightedNodes(inputFormatted)
    : null;
  const outputNodes = outputFormatted
    ? getHighlightedNodes(outputFormatted)
    : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl  gap-0 rounded-lg border border-line-secondary p-0 [&>button:last-child]:hidden">
        <DialogHeader className="w-full flex-col gap-3 rounded-tl-lg rounded-tr-lg bg-[#122d37] py-2 pl-3 pr-[6px] text-left">
          <DialogTitle className="flex items-center justify-between">
            <div>
              <span className="text-body-lg font-normal text-[#EBEFF1]">
                Trace ID:
              </span>
              <span
                onClickCapture={() => window.setTimeout(onClose, 0)}
                className="inline-flex"
              >
                <TraceIdLink
                  block={block}
                  occurrenceKey={occurrenceKey}
                  copiedInfo={copiedInfo}
                  setCopiedInfo={setCopiedInfo}
                />
              </span>
            </div>
            <div className="flex gap-2">
              <SearchWithNavigation
                value={term}
                onChange={setTerm}
                onClear={handleClearSearch}
                placeholder="Search keywords"
                searchWidth="w-full sm:w-56"
                matches={matches}
                currentIndex={currentIndex}
                onPreviousMatch={prevMatch}
                onNextMatch={nextMatch}
                disabled={false}
              />
              <DialogClose className="shrink-0 text-body-subtle">
                {KahuIcons.KahuModalCrossIcon()}
              </DialogClose>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6 rounded-lg bg-[#08242F]">
          <Section label="Input" nodes={inputNodes} />
          <Section label="Output" nodes={outputNodes} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
